import type { FinancialYear } from "../types.js";
import type { IncomeComponents } from "../income-tests/index.js";
import { deriveAu08Income } from "./derive-income.js";
import { fy2025_26 } from "./fy2025-26.js";
import type { Au08Input, Au08RuleSet } from "./types.js";

// ============================================================================
//  AU-08 ruleset for FY2026-27 (1 July 2026 – 30 June 2027).
//
//  ⚠️⚠️  DATA GATE — DO NOT SHIP TO USERS  ⚠️⚠️
//
//  This ruleset exists to wire AU-08's MLS income through the shared
//  `income-tests` engine (Item 1 follow-on): the MLS income figure is DERIVED
//  from raw components via `incomeForSurchargePurposes`, not passed in pre-
//  derived. That income-derivation wiring is REAL and tested.
//
//  HOWEVER, the MLS THRESHOLD TABLES below are a PLACEHOLDER — they are a copy
//  of the shipped FY2025-26 single/family bands. The official ATO FY2026-27
//  MLS income thresholds and rates may not be published yet, and we do NOT
//  invent figures (regulatory guardrail: every output cites a real source).
//
//  Until the real FY2026-27 thresholds land, this ruleset MUST NOT back a live
//  user-facing calculation. The exported `FY2026_27_THRESHOLDS_ARE_PLACEHOLDER`
//  flag and the `placeholderThresholds` field exist so callers can refuse to
//  serve it. It is intentionally NOT registered in au08/index.ts's
//  AU08_RULESETS / AU08_CURRENT_FY (so `au08RulesFor("2026-27")` still throws)
//  until the data gate is cleared.
//
//  TODO(data): replace the placeholder single/family bands + familyChildIncrement
//  with the published ATO FY2026-27 MLS income thresholds and rates, set
//  `sources.mls` to the FY2026-27 page, update `sourcesVerifiedAt`, flip
//  `placeholderThresholds` to false, drop the PLACEHOLDER flag, and register
//  this ruleset in au08/index.ts. Source to watch:
//  https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/medicare-levy-surcharge-income-thresholds-and-rates
// ============================================================================

/** FY this placeholder ruleset is authored for. */
export const FY2026_27: FinancialYear = "2026-27";

/**
 * LOUD data-gate flag. While `true`, the FY2026-27 MLS thresholds are a copy of
 * FY2025-26 and the ruleset must NOT serve live user calculations. Callers (API
 * layer) should assert this is `false` before using `fy2026_27` for a user.
 */
export const FY2026_27_THRESHOLDS_ARE_PLACEHOLDER = true;

export const fy2026_27: Au08RuleSet & { placeholderThresholds: boolean } = {
  product: "AU08",
  fy: "2026-27",
  effectiveFrom: "2026-07-01",
  effectiveTo: "2027-06-30",
  // LHC age math uses the calendar year the FY starts in.
  assessmentYear: 2026,
  sources: {
    // TODO(data): point at the FY2026-27 MLS thresholds page once published.
    // This still references the FY2025-26 page because the figures below are
    // the FY2025-26 placeholder copy — NOT verified FY2026-27 data.
    mls: fy2025_26.sources.mls!,
    mlsFamilyExample: fy2025_26.sources.mlsFamilyExample!,
    mlsAppropriateCover: fy2025_26.sources.mlsAppropriateCover!,
    lhc: fy2025_26.sources.lhc!,
  },
  // NOT a verification of FY2026-27 figures — these are placeholders.
  sourcesVerifiedAt: "PLACEHOLDER — FY2026-27 MLS thresholds not yet published",

  // PLACEHOLDER thresholds: copied verbatim from the shipped FY2025-26 ruleset.
  // TODO(data): replace with published ATO FY2026-27 MLS thresholds + rates.
  mls: {
    single: fy2025_26.mls.single,
    family: fy2025_26.mls.family,
    familyChildIncrement: fy2025_26.mls.familyChildIncrement,
  },
  // LHC loading rules are structural (2%/yr, 70% cap, 10-yr dropoff, grace age
  // 31) and have been stable for years — not threshold data — so reusing them
  // is safe even before the FY2026-27 MLS tables land.
  lhc: { ...fy2025_26.lhc },

  /** True while the MLS threshold tables above are FY2025-26 placeholders. */
  placeholderThresholds: FY2026_27_THRESHOLDS_ARE_PLACEHOLDER,
};

/**
 * Derive "income for MLS purposes" for FY2026-27 from raw components, via the
 * shared income-tests engine. This is the Item-1 wiring: the income figure fed
 * to `calculateAu08` is computed from components, not passed pre-derived.
 *
 * NOTE: the income DERIVATION is correct for FY2026-27 (the surcharge income
 * definition is structural and the FBT-year factor doesn't enter it); only the
 * MLS THRESHOLD comparison is gated by the placeholder above.
 */
export function deriveFy2026_27MlsIncome(components: IncomeComponents): number {
  return deriveAu08Income(components, FY2026_27);
}

/** Convenience: a `calculateAu08` input from raw components for FY2026-27. */
export type Au08Fy2026_27ComponentInput = {
  self: IncomeComponents;
  partner?: IncomeComponents;
} & Omit<Au08Input, "income" | "partnerIncome">;
